import {computed, effect, inject, Injectable, signal, untracked} from '@angular/core';
import {CompositionItem} from '../models/composition-item.model';
import {ConnectionSide} from '../enums/connection-side.enum';
import {ModelElement} from '../models/model-element.model';
import {ComposerEngineService} from './composer-engine.service';
import {hasConnector} from '@app/model-composer';
import {VestaBridgeService} from '@app/vesta/services/vesta-bridge.service';
import {CompositionSelectionService} from '@app/model-composer/services/composition-selection.service';
import {IpeDebug} from '@app/model-composer/ipe-debug';

@Injectable({ providedIn: 'root' })
export class ComposerStateService {
  private engine = inject(ComposerEngineService);
  private vestaBridge = inject(VestaBridgeService);
  private selectionService = inject(CompositionSelectionService);
  private _composition = signal<CompositionItem[]>([]);
  private _activeSide = signal<ConnectionSide | null>(ConnectionSide.Right);
  private _activeItem = signal<CompositionItem | null>(null);
  private _oldCompositionId = signal<number | null>(null);
  readonly oldCompositionId = this._oldCompositionId.asReadonly();
  readonly composition = this._composition.asReadonly();
  readonly activeSide = this._activeSide.asReadonly();
  readonly activeItem = this._activeItem.asReadonly();
  readonly vestaSet = computed(() =>
    this._composition().map(item => {
      const config = !item?.element?.originalPlacements
        ? item.element.activePlacements
        : item.element.placements;

      return {
        id: item.uid,
        teapotId: item.element.externalLink,
        config: Array.isArray(config) ? [...config] : [],
      };
    })
  );

  constructor() {
    effect(() => {
      const side = this._activeSide();
      const ready = this.vestaBridge.isAttached();
      const hasFn = this.vestaBridge.hasExternalUpdateClicked();
      if (!side || !ready || !hasFn) { return; }

      untracked(() => {
        this.vestaBridge.callExternalUpdateClickedForSide(side);
      });
    });

    effect(() => {
      const comp = this.composition();
      const active = this.activeItem();

      const activeFromComposition = active
        ? comp.find(i => i.uid === active.uid)
        : null;

      IpeDebug.event('state.composition.changed', {
        compositionLength: comp.length,
        activeUid: active?.uid ?? null,
        activeItem: activeFromComposition
          ? IpeDebug.itemSnapshot(activeFromComposition)
          : null,
      });
    });
  }
  openSelector(side: ConnectionSide): void {
    this._activeSide.set(side);
  }

  selectItem(element: CompositionItem): void {
    this._activeItem.set(element);
  }

  highlightItem(uid: string): void {
    const item = this._composition().find(i => i.uid === uid) ?? null;
    this._activeItem.set(item);
  }

  closeSelector(): void {
    this._activeSide.set(null);
  }

  reset(): void {
    this._composition.set([]);
    this._oldCompositionId.set(null);
    this._activeItem.set(null);
    this._activeSide.set(ConnectionSide.Right);
    this.selectionService.resetAll();
  }

  canSelect(el: ModelElement): boolean {
    return this.engine.canSelect(
      el,
      this._composition(),
      this._activeSide()
    );
  }

  add(el: ModelElement): void {
    const side = this._activeSide();
    if (!side) { return; }
    const prevLength = this._composition().length;
    const nextComposition = this.engine.add(el, this._composition(), side);
    this._composition.set(nextComposition);
    if (nextComposition.length > prevLength) {
      const addedItem =
        side === ConnectionSide.Right
          ? nextComposition[nextComposition.length - 1]
          : nextComposition[0];
      if (addedItem) {
        this.selectionService.applyDefaultsFromElement(addedItem.uid, addedItem.element);
      }
    }
    const preferred = side;
    const canOpenOn = (s: ConnectionSide): boolean => {
      if (!nextComposition.length) { return false; }
      const endEl = s === ConnectionSide.Right
        ? nextComposition[nextComposition.length - 1].element
        : nextComposition[0].element;
      return hasConnector(endEl.type, s);
    };
    let nextSide: ConnectionSide | null = null;
    if (canOpenOn(preferred)) {
      nextSide = preferred;
    } else {
      const other = preferred === ConnectionSide.Right ? ConnectionSide.Left : ConnectionSide.Right;
      nextSide = canOpenOn(other) ? other : null;
    }
    this._activeSide.set(nextSide);
  }

  remove(item: any): void {
    this.selectionService.clearElement(item.uid);
    this._composition.set(this._composition().filter(i => i !== item));
    this._activeItem.set(null);
    if (!this._composition()?.length) {
      this._activeSide.set(ConnectionSide.Right);
    }
  }

  applyOptionalToComposition(p: any): void {
    const placementToAdd = (p.optionalPlacements ?? []).filter(Boolean);
    const placementToRemove = (p.placementsToRemove ?? []).filter(Boolean);
    const qty = Math.max(1, Number(p.quantity ?? 1) || 1);

    IpeDebug.event(
      'optional.apply.start',
      {
        uid: p?.uid ?? null,
        enabled: p?.enabled,
        quantity: p?.quantity,
        resolvedQty: qty,
        placementToAdd: [...placementToAdd],
        placementToRemove: [...placementToRemove],
        rawPayload: p,
      },
      { stack: true }
    );

    if (!placementToAdd.length && !placementToRemove.length) {
      IpeDebug.event('optional.apply.skip', {
        reason: 'no placementToAdd and no placementToRemove',
        uid: p?.uid ?? null,
      });
      return;
    }

    const curr = this._composition();
    const idx = curr.findIndex(x => x.uid === p.uid);

    if (idx < 0) {
      IpeDebug.event('optional.apply.skip', {
        reason: 'uid not found in composition',
        uid: p?.uid ?? null,
        compositionUids: curr.map(x => x.uid),
      });
      return;
    }

    const item = curr[idx];
    const element = item?.element;

    const asArray = <T = string>(value: unknown): T[] =>
      Array.isArray(value) ? [...value] as T[] : [];

    const isAlreadyCustomized = !!element?.originalPlacements;

    const originalPlacements = asArray<string>(
      isAlreadyCustomized
        ? element?.originalPlacements
        : element?.activePlacements
    );

    const basePlacements = asArray<string>(
      isAlreadyCustomized
        ? element?.placements
        : element?.activePlacements
    );

    const beforeSnapshot = IpeDebug.itemSnapshot(item);

    const placementToAddSet = new Set(placementToAdd);
    const placementToRemoveSet = new Set(placementToRemove);

    let nextPlacements = basePlacements.filter(plc => !placementToAddSet.has(plc));

    if (p.enabled) {
      nextPlacements = nextPlacements.filter(plc => !placementToRemoveSet.has(plc));

      for (let i = 0; i < qty; i++) {
        nextPlacements.push(...placementToAdd);
      }
    } else {
      const existing = new Set(nextPlacements);

      for (const plc of placementToRemove) {
        if (!existing.has(plc)) {
          nextPlacements.push(plc);
          existing.add(plc);
        }
      }
    }

    const nextItem = {
      ...item,
      element: {
        ...item.element,
        placements: nextPlacements,
        originalPlacements,
      },
    };

    IpeDebug.event('optional.apply.calculated', {
      uid: p.uid,
      enabled: p.enabled,
      isAlreadyCustomized,
      before: beforeSnapshot,
      basePlacements: [...basePlacements],
      originalPlacements: [...originalPlacements],
      nextPlacements: [...nextPlacements],
      diffFromBase: IpeDebug.arrayDiff(basePlacements, nextPlacements),
    });

    const next = curr.slice();
    next[idx] = nextItem;

    this._composition.set(next);

    if (this._activeItem()?.uid === p.uid) {
      this._activeItem.set(nextItem);
    }

    IpeDebug.event('optional.apply.end', {
      uid: p.uid,
      enabled: p.enabled,
      after: IpeDebug.itemSnapshot(nextItem),
    });
  }
  resetSelection(): void {
    this._activeItem.set(null);
  }

  canAddLeft(): boolean {
    const c = this._composition();
    if (!c.length) { return true; }
    const t = c[0].element.type;
    return t === 'C' || t === 'R';
  }

  canAddRight(): boolean {
    const c = this._composition();
    if (!c.length) { return true; }
    const t = c[c.length - 1].element.type;
    return t === 'C' || t === 'L';
  }

  canRemoveAt(index: number): boolean {
    const len = this.composition().length;
    if (len === 0) { return false; }
    return index === 0 || index === len - 1;
  }

  setComposition(composition: CompositionItem[], compositionId: number | null): void {
    IpeDebug.event(
      'state.setComposition',
      {
        compositionId,
        items: composition.map(item => IpeDebug.itemSnapshot(item)),
      },
      { stack: true }
    );

    this._composition.set(composition);
    this._oldCompositionId.set(compositionId);
  }

  applyMaterialPlacementSwapToComposition(payload: {
    placementsToRemove?: { code: string; placements: string[] }[];
    placementsToRestore?: { code: string; placements: string[] }[];
  }): void {
    const removeByCode = new Map(
      (payload.placementsToRemove ?? []).map(x => [x.code, x.placements ?? []])
    );

    const restoreByCode = new Map(
      (payload.placementsToRestore ?? []).map(x => [x.code, x.placements ?? []])
    );

    if (!removeByCode.size && !restoreByCode.size) { return; }

    const curr = this._composition();

    const next = curr.map(item => {
      const code = item?.element?.code;
      if (!code) { return item; }

      const placementsToRemove = removeByCode.get(code) ?? [];
      const placementsToRestore = restoreByCode.get(code) ?? [];

      if (!placementsToRemove.length && !placementsToRestore.length) {
        return item;
      }

      const originalPlacements = Array.isArray(item.element.originalPlacements)
        ? [...item.element.originalPlacements]
        : [...(item.element.placements ?? item.element.activePlacements ?? [])];

      const alreadyCustomized = Array.isArray(item.element.originalPlacements);

      const basePlacements = alreadyCustomized
        ? [...(item.element.placements ?? [])]
        : [...originalPlacements];

      const removeSet = new Set(placementsToRemove);

      const nextPlacements = basePlacements.filter(plc => !removeSet.has(plc));

      for (const plc of placementsToRestore) {
        if (!nextPlacements.includes(plc)) {
          nextPlacements.push(plc);
        }
      }

      return {
        ...item,
        element: {
          ...item.element,
          originalPlacements,
          placements: nextPlacements,
        },
      };
    });

    this._composition.set(next);

    const activeUid = this._activeItem()?.uid;
    if (activeUid) {
      this._activeItem.set(next.find(x => x.uid === activeUid) ?? null);
    }
  }

  resetMaterialPlacementSwapFromComposition(payload: {
    placementsToRemove?: { code: string; placements: string[] }[];
    placementsToRestore?: { code: string; placements: string[] }[];
  }): void {
    const restoreOriginalByCode = new Map(
      (payload.placementsToRemove ?? []).map(x => [x.code, x.placements ?? []])
    );

    const removeSwappedByCode = new Map(
      (payload.placementsToRestore ?? []).map(x => [x.code, x.placements ?? []])
    );

    const curr = this._composition();

    const next = curr.map(item => {
      const code = item?.element?.code;
      if (!code) { return item; }

      const placementsToRestore = restoreOriginalByCode.get(code) ?? [];
      const placementsToRemove = removeSwappedByCode.get(code) ?? [];

      if (!placementsToRestore.length && !placementsToRemove.length) {
        return item;
      }

      const basePlacements = [
        ...(item.element.placements ?? item.element.activePlacements ?? []),
      ];

      const removeSet = new Set(placementsToRemove);

      const nextPlacements = basePlacements.filter(plc => !removeSet.has(plc));

      for (const plc of placementsToRestore) {
        if (!nextPlacements.includes(plc)) {
          nextPlacements.push(plc);
        }
      }

      return {
        ...item,
        element: {
          ...item.element,
          placements: nextPlacements,
        },
      };
    });

    this._composition.set(next);

    const activeUid = this._activeItem()?.uid;
    if (activeUid) {
      this._activeItem.set(next.find(x => x.uid === activeUid) ?? null);
    }
  }
}
